import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';

import type {ManualEvaluation} from '../../shared/FrqEvaluationSummary/FrqEvaluationSummary.types';

import useStudentGuess from './useStudentGuess';

type GuessEvaluation = NonNullable<
  ReturnType<typeof useStudentGuess>['guess']
>['teacherEvaluation'];

/** GraphQL nulls → the FE optional-field convention the summary renders from. */
function toManualEvaluation(evaluation: NonNullable<GuessEvaluation>): ManualEvaluation {
  return {
    overallScore: evaluation.overallScore ?? undefined,
    overallFeedback: evaluation.overallFeedback ?? undefined,
    criteriaScores: (evaluation.criteriaScores ?? [])
      .filter((criterion): criterion is NonNullable<typeof criterion> => criterion != null)
      .map((criterion) => ({
        criterionId: criterion.criterionId,
        score: criterion.score ?? undefined,
        feedback: criterion.feedback ?? undefined
      }))
  };
}

/**
 * The student's own released grade for this question, or null while there is nothing they may see,
 * with `isLoading` for callers that render something else when there is no grade.
 *
 * Gated on `gradeAvailable` rather than `evaluationReleased`, because it also folds in the
 * assignment's show-correct-answers setting: a grade released before the due date stays hidden
 * until that setting opens. It is the same condition the field policy on `teacherEvaluation`
 * applies, so a true here means the evaluation actually came back.
 *
 * `isLoading` is narrower than "a query is in flight": it is true only while a grade could still
 * arrive. A teacher, a disabled flag, or a question with no guess of its own can never produce one,
 * so those settle at once rather than waiting on reads whose results cannot change the answer.
 */
export default function useFrqReleasedGrade(questionId: string | undefined): {
  evaluation: ManualEvaluation | null;
  isLoading: boolean;
} {
  const isStudent = useFluxStore(sessionStore, (s) => s.isStudent());
  const {isFeatureEnabled, isLoading: isFlagLoading} =
    useIsFeatureFlagEnabled('frq_teacher_grading');
  const {guess, loading: isGuessLoading} = useStudentGuess(questionId);

  const couldHaveGrade = isStudent && (isFeatureEnabled || isFlagLoading);
  const isLoading = couldHaveGrade && (isGuessLoading || (isFlagLoading && guess != null));

  if (!isFeatureEnabled || !isStudent || guess?.gradeAvailable !== true) {
    return {evaluation: null, isLoading};
  }

  return {
    evaluation: guess.teacherEvaluation ? toManualEvaluation(guess.teacherEvaluation) : null,
    isLoading
  };
}
